import { createSelector } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/query';

import type {
  AddressListItem,
  MenuItem,
  PartyAddress,
} from '../../types/api-types';
import { isOktaAdminEmployeeUser } from '../../utils/auth-utils';
import { getSalesAgreement } from '../../utils/core-utils';
import accountApi from '../apis/account-api';
import customerNetApi from '../apis/cnet-api';
import productApi from '../apis/product-api';
import userApi from '../apis/user-api';
import type { AppRootState } from '../store';

export const activeUserSelector = (state: AppRootState) => state.auth.user;

export const userDetailsSelector = createSelector(
  [
    (state: AppRootState) => {
      const user = activeUserSelector(state);
      return customerNetApi.user.endpoints.userDetails.select(
        user ? { userId: user.sso } : skipToken
      )(state);
    },
  ],
  userDetailsResult => userDetailsResult.data?.responseObject?.[0]
);

const localUserDetailsResultSelector = (state: AppRootState) => {
  const activeUser = activeUserSelector(state);
  const userDetails = userDetailsSelector(state);
  return (
    activeUser &&
    userDetails &&
    customerNetApi.user.endpoints.localUserDetails.select({
      personPartyId: userDetails.PERSON_PARTY_ID,
      userId: activeUser.sso,
    })(state)
  );
};

export const localUserDetailsSelector = createSelector(
  [localUserDetailsResultSelector],
  localUserDetailsResult => {
    return localUserDetailsResult?.data?.responseObject;
  }
);

const accountDetailsArgsSelector = createSelector(
  [activeUserSelector],
  activeUser => {
    if (!activeUser?.selectedAccountNumber) {
      return skipToken;
    }
    return {
      accountNumber: activeUser.selectedAccountNumber,
      ssoId: activeUser.sso,
    };
  }
);

const accountDetailsFromActiveUserResultSelector = (state: AppRootState) => {
  const accountDetailsArgs = accountDetailsArgsSelector(state);
  return customerNetApi.springBoot.endpoints.accountDetails.select(
    accountDetailsArgs
  )(state);
};

const accountDetailsFromActiveUserSelector = createSelector(
  [accountDetailsFromActiveUserResultSelector],
  accountDetailsFromActiveUserResult =>
    accountDetailsFromActiveUserResult.data?.responseObject?.[0]
);

const addressListArgsSelector = createSelector(
  [activeUserSelector, accountDetailsFromActiveUserSelector],
  (activeUser, accountDetailsFromActiveUser) => {
    if (!activeUser?.selectedAccountNumber || !accountDetailsFromActiveUser) {
      return skipToken;
    }
    return {
      accountNumber: activeUser.selectedAccountNumber,
      partyId: accountDetailsFromActiveUser.PARTYID,
    };
  }
);

const addressListResultSelector = (state: AppRootState) => {
  const addressListArgs = addressListArgsSelector(state);
  return customerNetApi.springBoot.endpoints.addressList.select(
    addressListArgs
  )(state);
};

const addressListSelector = createSelector(
  [addressListResultSelector],
  addressListResult => addressListResult.data?.responseObject
);

const addressListToPropertiesSelector = createSelector(
  [accountDetailsFromActiveUserSelector, addressListSelector],
  (accountDetailsFromActiveUser, addressList) =>
    addressList
      ?.filter(address => address.PURPOSE_STATUS?.toUpperCase() !== 'I')
      .map(address =>
        mapAddressListItemToPartyAddress(
          address,
          accountDetailsFromActiveUser?.ORGANIZATIONNAME
        )
      )
);

const propertiesArgsSelector = createSelector(
  [userDetailsSelector],
  userDetails => {
    if (!userDetails) {
      return skipToken;
    }
    return { partyId: userDetails.PERSON_PARTY_ID };
  }
);

const propertiesResultSelector = (state: AppRootState) => {
  const propertiesArgs = propertiesArgsSelector(state);
  return accountApi.endpoints.addressesByPartyId.select(propertiesArgs)(state);
};

export const propertiesSelector = createSelector(
  [
    activeUserSelector,
    propertiesResultSelector,
    addressListToPropertiesSelector,
  ],
  (activeUser, propertiesResult, addressListToProperties) => {
    const properties = propertiesResult.data?.responseObject.filter(
      property =>
        property.RELATIONSHIP_STATUS === 'A' && property.ACCT_STATUS === 'A'
    );

    if (properties?.length || !isOktaAdminEmployeeUser(activeUser)) {
      return properties;
    }

    if (!activeUser?.selectedAccountNumber) {
      return undefined;
    }

    return addressListToProperties;
  }
);

const propertyGroupsResultSelector = (state: AppRootState) => {
  const activeUser = activeUserSelector(state);
  return userApi.endpoints.propertyGroups.select(
    activeUser ? { userId: activeUser.sso } : skipToken
  )(state);
};

function mapAddressListItemToPartyAddress(
  address: AddressListItem,
  customerName?: string
): PartyAddress {
  return {
    CUSTOMER_NUMBER: '',
    CUSTOMER_TYPE: '',
    CUSTOMER_NAME: customerName || '',
    CUSTOMER_STATUS: '',
    CONTACT_NUMBER: '',
    PERSON_FIRST_NAME: '',
    PERSON_LAST_NAME: '',
    CONTACT_PARTY_STATUS: '',
    CONTACT_ROLE_STATUS: '',
    RELATIONSHIP_STATUS: 'A',
    CONTACT_PARTY_NAME: '',
    CONTACT_STATUS: '',
    ORG_CONTACT_ID: 0,
    PARTY_RELATIONSHIP_ID: 0,
    SUBJECT_TYPE: '',
    OBJECT_TYPE: '',
    SUBJECT_TABLE_NAME: '',
    OBJECT_TABLE_NAME: '',
    RELATIONSHIP_CODE: '',
    RELATIONSHIP_TYPE: '',
    CUST_ACCOUNT_ROLE_ID: 0,
    CURRENT_ROLE_STATE: '',
    CUST_ACCT_SITE_ID: address.SITEID,
    ROLE_TYPE: '',
    ACCOUNT_NUMBER: address.ACCOUNT_NUMBER,
    PARTY_SITE_NUMBER: address.SITENUMBER,
    ADDRESS1: address.ADDRESS1,
    CITY: address.CITY,
    STATE: address.STATE,
    COUNTY: address.COUNTY,
    COUNTRY: address.COUNTRY,
    POSTAL_CODE: address.POSTALCODE,
    SITE_USE_ID: address.SITE_USE_ID,
    SITE_USE_CODE: address.PURPOSE as PartyAddress['SITE_USE_CODE'],
    SALES_CHANNEL_CODE: '',
    CUSTOMER_CLASS_CODE: '',
    ACCT_STATUS: address.STATUS,
    LOCATION_ID: address.LOCATIONID,
  } as PartyAddress;
}

const getOktaActiveProperty = (state: AppRootState) => {
  if (!isOktaAdminEmployeeUser(state.auth.user)) {
    return undefined;
  }

  const user = activeUserSelector(state);
  if (!user?.selectedAccountNumber) {
    return undefined;
  }

  const accountDetails =
    customerNetApi.springBoot.endpoints.accountDetails.select({
      accountNumber: user.selectedAccountNumber,
      ssoId: user.sso,
    })(state).data?.responseObject?.[0];

  const addressList =
    accountDetails &&
    customerNetApi.springBoot.endpoints.addressList.select({
      accountNumber: user.selectedAccountNumber,
      partyId: accountDetails.PARTYID,
    })(state).data?.responseObject;

  const selectedAddress =
    addressList?.find(
      address =>
        !!user.activePropertyId &&
        address.SITE_USE_ID.toString() === user.activePropertyId
    ) ||
    addressList?.find(address => address.PURPOSE.toUpperCase() === 'SHIP_TO');

  return selectedAddress
    ? mapAddressListItemToPartyAddress(
        selectedAddress,
        accountDetails?.ORGANIZATIONNAME
      )
    : undefined;
};

export const propertyGroupsSelector = createSelector(
  [
    propertiesSelector,
    propertyGroupsResultSelector,
    activeUserSelector,
    (state: AppRootState) => getOktaActiveProperty(state),
  ],
  (properties, propertyGroupsResult, activeUser, oktaActiveProperty) => {
    const rawGroups = propertyGroupsResult.data?.responseObject || [];

    const propertyGroupsAcc: {
      groups: Record<
        string,
        (PartyAddress & { isLastUsed?: boolean; sortOrder?: number })[]
      >;
      ungrouped: (PartyAddress & {
        isLastUsed?: boolean;
        sortOrder?: number;
      })[];
    } = { groups: {}, ungrouped: [] };

    const groupLookup = new Map<
      string,
      { groupName: string; isLastUsed: boolean; sortOrder: number }
    >();
    rawGroups.forEach(item =>
      groupLookup.set(item.siteUsedId, {
        groupName: item.groupName,
        isLastUsed: item.lastUsedAddress,
        sortOrder: item.sortOrder,
      })
    );

    const sourceProperties =
      properties?.length || !isOktaAdminEmployeeUser(activeUser)
        ? properties
        : oktaActiveProperty
          ? [oktaActiveProperty]
          : [];

    sourceProperties?.forEach(property => {
      if (property.SITE_USE_CODE !== 'SHIP_TO') {
        return;
      }

      const group = groupLookup.get(property.SITE_USE_ID.toString());

      if (group?.groupName) {
        if (!propertyGroupsAcc.groups[group.groupName]) {
          propertyGroupsAcc.groups[group.groupName] = [];
        }
        propertyGroupsAcc.groups[group.groupName].push({
          ...property,
          isLastUsed: group.isLastUsed,
          sortOrder: group.sortOrder,
        });
      } else if (group) {
        propertyGroupsAcc.ungrouped.push({
          ...property,
          isLastUsed: group.isLastUsed,
          sortOrder: group.sortOrder,
        });
      } else {
        propertyGroupsAcc.ungrouped.push({ ...property });
      }
    });

    return propertyGroupsAcc;
  }
);

export const favoritesResultSelector = (state: AppRootState) => {
  const activeUser = activeUserSelector(state);

  return productApi.endpoints.favorites.select(
    activeUser ? { userId: activeUser.sso } : skipToken
  )(state);
};

export const favoritesSelector = createSelector(
  [favoritesResultSelector],
  favoritesResult => {
    return favoritesResult.data;
  }
);

export const activeListsResultSelector = (state: AppRootState) => {
  const properties = propertiesSelector(state);
  const accountNumbers = properties
    ?.filter(property => property.SITE_USE_CODE === 'SHIP_TO')
    .map(property => property.ACCOUNT_NUMBER)
    .toSorted((a, b) => +a - +b);

  return productApi.endpoints.lists.select(
    accountNumbers ? { accountNumbers } : skipToken
  )(state);
};

export const activeListsSelector = createSelector(
  [activeListsResultSelector],
  activeListsResult => {
    return activeListsResult.data;
  }
);

export const packagesResultSelector = (state: AppRootState) => {
  const properties = propertiesSelector(state);
  const accountNumbers = properties
    ?.filter(property => property.SITE_USE_CODE === 'SHIP_TO')
    .map(property => property.ACCOUNT_NUMBER)
    .toSorted((a, b) => +a - +b);

  return productApi.endpoints.packages.select(
    accountNumbers ? { accountNumbers } : skipToken
  )(state);
};

export const packagesSelector = createSelector(
  [packagesResultSelector],
  packagesResult => {
    return packagesResult.data;
  }
);

export const activePropertySelector = createSelector(
  [propertiesSelector, activeUserSelector],
  (properties, activeUser) => {
    const activeProperty = properties?.find(
      address =>
        activeUser?.activePropertyId &&
        address.SITE_USE_ID === +activeUser?.activePropertyId
    );

    return activeProperty;
  }
);

const activeAccountResultSelector = (state: AppRootState) => {
  const user = activeUserSelector(state);
  const activeProperty = activePropertySelector(state);
  const selectedAccountNumber =
    activeProperty?.ACCOUNT_NUMBER ||
    (isOktaAdminEmployeeUser(user) ? user?.selectedAccountNumber : undefined);
  return customerNetApi.springBoot.endpoints.accountDetails.select(
    user && selectedAccountNumber
      ? {
          accountNumber: selectedAccountNumber,
          ssoId: user.sso,
        }
      : skipToken
  )(state);
};

const activeAccountPartyRelationshipsResultSelector = (state: AppRootState) => {
  const activeAccount = activeAccountResultSelector(state);
  const partyId =
    activeAccount && activeAccount.data?.responseObject?.[0]?.PARTYID;
  return (
    partyId &&
    customerNetApi.springBoot.endpoints.partyRelationships.select({
      partyId: partyId,
    })(state)
  );
};

export const activeAccountPartyRelationshipsSelector = createSelector(
  [activeAccountPartyRelationshipsResultSelector],
  activeAccountPartyRelationshipsResult => {
    return activeAccountPartyRelationshipsResult
      ? activeAccountPartyRelationshipsResult.data?.responseObject
      : [];
  }
);

export const activeAccountSelector = createSelector(
  [activeAccountResultSelector],
  activeAccountResult => activeAccountResult.data?.responseObject?.[0]
);

export const billingAddressSelector = (state: AppRootState) => {
  const activeProperty = activePropertySelector(state);
  const properties = propertiesSelector(state);

  return properties?.find(
    property =>
      property.ACCOUNT_NUMBER === activeProperty?.ACCOUNT_NUMBER &&
      property.SITE_USE_CODE === 'BILL_TO'
  );
};

const salesAgreementsResultSelector = (state: AppRootState) => {
  const activeProperty = activePropertySelector(state);
  const user = activeUserSelector(state);
  const selectedAccountNumber =
    activeProperty?.ACCOUNT_NUMBER ||
    (isOktaAdminEmployeeUser(user) ? user?.selectedAccountNumber : undefined);
  return (
    selectedAccountNumber &&
    customerNetApi.springBoot.endpoints.salesAgreements.select({
      accountNumber: selectedAccountNumber,
    })(state)
  );
};

export const salesAgreementsSelector = createSelector(
  [salesAgreementsResultSelector],
  result => {
    if (!result || typeof result === 'string') return undefined;
    return result.data?.responseObject;
  }
);

export const shippingLocationsSelector = createSelector(
  [
    (state: AppRootState) => {
      const activeProperty = activePropertySelector(state);
      return (
        activeProperty &&
        customerNetApi.springBoot.endpoints.shippingLocations.select({
          accountNumber: activeProperty.ACCOUNT_NUMBER,
          state: activeProperty.STATE,
          countryCode: activeProperty.COUNTRY,
          postalCode: activeProperty.POSTAL_CODE.split('-')[0],
        })(state)
      );
    },
  ],
  shippingResult => {
    return shippingResult?.data?.responseObject;
  }
);

export const activeCartResultSelector = (state: AppRootState) => {
  const activeProperty = activePropertySelector(state);
  return (
    activeProperty &&
    productApi.endpoints.cart.select({
      accountNumber: activeProperty.ACCOUNT_NUMBER,
    })(state)
  );
};

export const activeCartSelector = createSelector(
  [activeCartResultSelector],
  activeCartResult => {
    return activeCartResult?.data?.responseObject;
  }
);

export const activeSavedForLaterResultSelector = (state: AppRootState) => {
  const activeProperty = activePropertySelector(state);
  return (
    activeProperty &&
    productApi.endpoints.savedForLater.select({
      accountNumber: activeProperty.ACCOUNT_NUMBER,
    })(state)
  );
};

export const activeSavedForLaterSelector = createSelector(
  [activeSavedForLaterResultSelector],
  activeSavedForLaterResult => {
    return activeSavedForLaterResult?.data?.responseObject;
  }
);

const menuResultSelector = (state: AppRootState) => {
  return productApi.endpoints.menu.select()(state);
};

export const menuSelector = createSelector([menuResultSelector], menu => {
  return menu.data;
});

export const orderableMenuResultSelector = createSelector(
  [salesAgreementsResultSelector, (state: AppRootState) => state],
  (_, state) => {
    const salesAgreement = getSalesAgreement('Standard Agreement', true);

    return productApi.endpoints.orderableMenu.select(
      salesAgreement
        ? {
            accountNumber: salesAgreement.ACCOUNT_NUMBER,
            agreementNumber: salesAgreement.AGREEMENT_NUMBER.toString(),
          }
        : skipToken
    )(state);
  }
);

export const partsOrderableMenuResultSelector = createSelector(
  [salesAgreementsResultSelector, (state: AppRootState) => state],
  (_, state) => {
    const partsSalesAgreement = getSalesAgreement('GEA Parts', true);

    return productApi.endpoints.orderableMenu.select(
      partsSalesAgreement
        ? {
            accountNumber: partsSalesAgreement.ACCOUNT_NUMBER,
            agreementNumber: partsSalesAgreement.AGREEMENT_NUMBER.toString(),
          }
        : skipToken
    )(state);
  }
);

const mergeMenuItems = (
  menuItems: MenuItem[],
  otherMenuItems: MenuItem[]
): MenuItem[] => {
  const mergedMenuItems = new Map(
    menuItems.map(menuItem => [menuItem.id, menuItem])
  );

  otherMenuItems.forEach(otherMenuItem => {
    const menuItem = mergedMenuItems.get(otherMenuItem.id);

    mergedMenuItems.set(
      otherMenuItem.id,
      menuItem
        ? {
            ...menuItem,
            active: menuItem.active || otherMenuItem.active,
            children:
              menuItem.children || otherMenuItem.children
                ? mergeMenuItems(
                    menuItem.children ?? [],
                    otherMenuItem.children ?? []
                  )
                : undefined,
          }
        : otherMenuItem
    );
  });

  return [...mergedMenuItems.values()].toSorted((a, b) => a.order - b.order);
};

export const orderableMenuSelector = createSelector(
  [
    (state: AppRootState) => orderableMenuResultSelector(state).data,
    (state: AppRootState) => partsOrderableMenuResultSelector(state).data,
    (state: AppRootState) =>
      orderableMenuResultSelector(state).isLoading ||
      partsOrderableMenuResultSelector(state).isLoading,
  ],
  (standardMenu, partsMenu, isLoading) => {
    // Wait for both so the menu doesn't render half and then change
    if (isLoading) {
      return undefined;
    }

    if (standardMenu && partsMenu) {
      return mergeMenuItems(standardMenu, partsMenu);
    }

    return standardMenu ?? partsMenu;
  }
);

const priceSubTypesResultSelector = (state: AppRootState) =>
  customerNetApi.partsSpringBoot.endpoints.priceSubTypes.select()(state);

export const priceSubTypesSelector = createSelector(
  [priceSubTypesResultSelector],
  priceSubTypesResult => priceSubTypesResult.data?.responseObject
);
